import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import type { SupabaseClient } from "@supabase/supabase-js";

import type { AlertRow } from "../data/alert-queue";
import { fetchAlertQueue } from "../data/alert-queue";
import {
  resolveActivityMapTarget,
  type ActivityMapTarget,
} from "../data/activity-map-focus";
import type {
  CommandFocusEventInput,
  CommandFocusSubject,
} from "../data/command-focus";
import {
  cadRealtimeFeed,
  countOnShift,
  countOpenAlerts,
  countOpenSosAlerts,
  countRecentScans,
  dockBadgePollMs,
} from "../data/cad-feed";
import {
  countOpenDutyCalls,
  fetchDutyCalls,
  type DutyCallSummary,
} from "../data/duty-calls";
import { fetchDutyTrackingPolicy } from "../data/duty-pings";
import { gpsAccuracyBandThresholds } from "../data/gps-accuracy-bands";
import {
  fetchCheckpointPoints,
  fetchScanPoints,
  checkpointPointFieldPin,
} from "../data/map-points";
import { fetchRoster } from "../data/roster";
import { fetchScanList, type ScanRow } from "../data/scan-list";
import { useReadModel } from "../data/useReadModel";
import {
  resolvePunchHighlight,
  takePunchHighlightForAccount,
  type StagedPunchHighlight,
} from "../lib/punch-map-handoff";
import type { PunchMapHighlight } from "../lib/punch-map-highlight";
import type { ShellOutletContext } from "../nav/AppShell";
import { AlertQueue } from "../surfaces/AlertQueue";
import { CadStatusBar } from "../surfaces/CadStatusBar";
import { SosResponseDeskPanel } from "../surfaces/SosResponseDesk";
import { LiveActivity } from "../surfaces/LiveActivity";
import { OnShiftRoster } from "../surfaces/OnShiftRoster";
import { DutyCallsDesk } from "../surfaces/DutyCallsDesk";
import { useSupportSession } from "../support/SupportSessionProvider";

const CommandMap = lazy(() =>
  import("../surfaces/CommandMap").then((module) => ({ default: module.CommandMap })),
);

interface CadMapFocus {
  securityContextKey: string;
  subject: CommandFocusSubject | null;
  selectedEvent: CommandFocusEventInput | null;
  nonce: number;
}

export function parseRequestedCadTile(value: string | null): "alerts" | "sos" | null {
  return value === "alerts" || value === "sos" ? value : null;
}

/**
 * The collapsible rails retired with the map-first desk; their persisted collapse
 * preferences are dead weight in operators' browsers. Removable once every fleet browser
 * has visited the glass desk at least once.
 */
const RETIRED_RAIL_PREFERENCE_KEYS = [
  "secops:cad:rail:roster-collapsed",
  "secops:cad:rail:alerts-collapsed",
] as const;

/**
 * The CAD command center — default landing (brief §8). Map-first glass desk
 * (design lock: qa/design/2026-08-10-cad-map-first-glass-overlays.html): the live map owns
 * the whole desk, the status strip floats over it, and On-shift / Calls / Alerts / Activity / SOS are
 * dock bubbles that open one glass tile at a time. Live is the resting state; Focus is one
 * bounded subject story on the same map. Realtime on scan/alert/roster base tables; polling
 * remains as fallback via config.refreshMs.
 */
export function CommandCenterPage() {
  const { client, config, can, isSuperadmin } = useOutletContext<ShellOutletContext>();
  const [searchParams] = useSearchParams();
  const { session: supportSession } = useSupportSession();
  const showDutyPresence = can("schedule.manage");
  // A1 authorizes supervisor/admin role or effective platform superadmin. Until Calls owns a
  // dedicated permission key, schedule.manage is the existing supervisor/admin affordance gate;
  // the membership-id RPC re-checks the authoritative role server-side.
  const canCreateDutyCallOffer = isSuperadmin || can("schedule.manage");
  // Receipts read `management_field_incident_list`, which already gates on evidence.view.
  // This flag is display honesty: omit the section rather than claiming the Call has no reports.
  // Deliberately narrower than the server predicate: a platform-access staff user satisfies
  // has_platform_account_access and would see the Incidents desk but gets no receipts section
  // here. That fails closed, which is the right direction for a permission this account can
  // revoke, and the Incidents desk remains the complete record either way.
  const canViewEvidence = isSuperadmin || can("evidence.view");
  const cadSecurityContextKey = `${config.accountId}:${supportSession?.sessionId ?? "direct"}`;
  const activeSecurityContextRef = useRef(cadSecurityContextKey);
  activeSecurityContextRef.current = cadSecurityContextKey;
  const requestedCadTile = parseRequestedCadTile(searchParams.get("cad"));
  const requestedAlertEventId = searchParams.get("alert");
  const requestAlertMapFocus = searchParams.get("focus") === "map";

  useEffect(() => {
    for (const key of RETIRED_RAIL_PREFERENCE_KEYS) {
      try {
        window.localStorage?.removeItem(key);
      } catch {
        // Private mode / disabled storage: nothing persisted, nothing to clean.
      }
    }
  }, []);

  /**
   * A punch the operator asked to see, handed over by the Timeclock report on /schedule. Taken
   * ONCE at mount: the handoff clears as it is read, so leaving CAD and coming back later shows
   * a clean map rather than replaying somebody's location. Usually null.
   *
   * Stamped with the workspace it came from. This page does not remount on a workspace switch,
   * so the stamp keeps the handoff safe independently of the keyed account-scoped desk.
   */
  const [stagedPunch, setStagedPunch] = useState<StagedPunchHighlight | null>(() =>
    takePunchHighlightForAccount(config.accountId),
  );
  const punchHighlight = resolvePunchHighlight(stagedPunch, config.accountId);
  const dismissPunchHighlight = useCallback(() => setStagedPunch(null), []);
  const [activityFocus, setActivityFocus] = useState<ActivityMapTarget | null>(null);
  const [selectedActivityEventId, setSelectedActivityEventId] = useState<string | null>(null);
  const [activityMapMessage, setActivityMapMessage] = useState<string | null>(null);
  const dismissActivityFocus = useCallback(() => {
    setActivityFocus(null);
    setSelectedActivityEventId(null);
    setActivityMapMessage(null);
  }, []);
  const [mapFocus, setMapFocus] = useState<CadMapFocus>({
    securityContextKey: cadSecurityContextKey,
    subject:
      punchHighlight?.routeAssignmentId !== null && punchHighlight?.routeAssignmentId !== undefined
        ? { kind: "officer", routeAssignmentId: punchHighlight.routeAssignmentId }
        : punchHighlight?.siteId !== null && punchHighlight?.siteId !== undefined
          ? { kind: "site", siteId: punchHighlight.siteId, label: "Site" }
          : null,
    selectedEvent:
      punchHighlight === null
        ? null
        : {
            id: `punch:${punchHighlight.timePunchId}`,
            kind: "punch",
            label: punchHighlight.label,
            occurredAt: punchHighlight.occurredAt ?? null,
            lat: punchHighlight.lat,
            lng: punchHighlight.lng,
          },
    nonce: 0,
  });
  const focusSecurityContextRef = useRef(cadSecurityContextKey);

  useEffect(() => {
    if (focusSecurityContextRef.current === cadSecurityContextKey) return;
    focusSecurityContextRef.current = cadSecurityContextKey;
    setMapFocus((previous) => ({
      securityContextKey: cadSecurityContextKey,
      subject: null,
      selectedEvent: null,
      nonce: previous.nonce + 1,
    }));
    setStagedPunch(null);
    setActivityFocus(null);
    setSelectedActivityEventId(null);
    setActivityMapMessage(null);
  }, [cadSecurityContextKey]);

  const dismissFocus = useCallback(() => {
    setMapFocus((previous) => ({
      securityContextKey: cadSecurityContextKey,
      subject: null,
      selectedEvent: null,
      nonce: previous.nonce + 1,
    }));
    setStagedPunch(null);
    setActivityFocus(null);
    setSelectedActivityEventId(null);
    setActivityMapMessage(null);
  }, [cadSecurityContextKey]);

  const focusRouteAssignment = useCallback((routeAssignmentId: string) => {
    setMapFocus((prev) => ({
      securityContextKey: cadSecurityContextKey,
      subject: { kind: "officer", routeAssignmentId },
      selectedEvent: null,
      nonce: prev.nonce + 1,
    }));
    setStagedPunch(null);
    setActivityFocus(null);
    setSelectedActivityEventId(null);
    setActivityMapMessage(null);
  }, [cadSecurityContextKey]);

  const focusSiteSubject = useCallback(
    (subject: Extract<CommandFocusSubject, { kind: "site" }>) => {
      setMapFocus((previous) => ({
        securityContextKey: cadSecurityContextKey,
        subject,
        selectedEvent: null,
        nonce: previous.nonce + 1,
      }));
      setStagedPunch(null);
      setActivityFocus(null);
      setSelectedActivityEventId(null);
      setActivityMapMessage(null);
    },
    [cadSecurityContextKey],
  );

  const focusAlertOnMap = useCallback((alert: AlertRow) => {
    setMapFocus((prev) => ({
      securityContextKey: cadSecurityContextKey,
      subject:
        alert.routeAssignmentId !== null
          ? {
              kind: "officer",
              routeAssignmentId: alert.routeAssignmentId,
              ...(alert.guardLabel === null ? {} : { label: alert.guardLabel }),
            }
          : alert.siteId !== null
            ? { kind: "site", siteId: alert.siteId, label: alert.siteName ?? "Site" }
            : null,
      selectedEvent:
        alert.alertType === "sos" && alert.dutyLat !== null && alert.dutyLng !== null
          ? {
              id: `sos:${alert.alertEventId}`,
              kind: "sos",
              label: `SOS${alert.guardLabel === null ? "" : ` · ${alert.guardLabel}`}`,
              occurredAt: alert.detectedAt,
              lat: alert.dutyLat,
              lng: alert.dutyLng,
            }
          : null,
      nonce: prev.nonce + 1,
    }));
    setStagedPunch(null);
    setActivityFocus(null);
    setSelectedActivityEventId(null);
    setActivityMapMessage(null);
  }, [cadSecurityContextKey]);

  const focusCallEventOnMap = useCallback(
    (call: DutyCallSummary, event: CommandFocusEventInput) => {
      setMapFocus((previous) => ({
        securityContextKey: cadSecurityContextKey,
        subject: { kind: "call", callId: call.callId, callRef: call.callRef },
        selectedEvent: event,
        nonce: previous.nonce + 1,
      }));
      setStagedPunch(null);
      setActivityFocus(null);
      setSelectedActivityEventId(null);
      setActivityMapMessage(null);
    },
    [cadSecurityContextKey],
  );

  const focusActivityOnMap = useCallback(
    async (row: ScanRow) => {
      setSelectedActivityEventId(row.clientEventId);
      setActivityMapMessage(null);
      // Clear a punch highlight so Activity is the sole temporary focus.
      setStagedPunch(null);
      try {
        const [scanPoints, checkpoints] = await Promise.all([
          fetchScanPoints(client, config.accountId),
          fetchCheckpointPoints(client, config.accountId),
        ]);
        if (activeSecurityContextRef.current !== cadSecurityContextKey) return;
        const fieldPins = checkpoints.map(checkpointPointFieldPin);
        const target = resolveActivityMapTarget(row, { scanPoints, fieldPins });
        if (target === null) {
          setActivityFocus(null);
          setActivityMapMessage(
            "No map location for this capture. Need scan GPS or a named checkpoint pin.",
          );
          return;
        }
        setActivityFocus(target);
        const subject: CommandFocusSubject | null =
          target.routeAssignmentId !== null && target.routeAssignmentId !== undefined
            ? {
                kind: "officer",
                routeAssignmentId: target.routeAssignmentId,
                ...(row.guardName === null ? {} : { label: row.guardName }),
              }
            : target.siteId !== null && target.siteId !== undefined
              ? { kind: "site", siteId: target.siteId, label: row.siteName ?? "Site" }
              : null;
        setMapFocus((previous) => ({
          securityContextKey: cadSecurityContextKey,
          subject,
          selectedEvent:
            target.source === "scan_gps"
              ? {
                  id: `scan:${target.scanPointId ?? (row.scanId || row.clientEventId)}`,
                  kind: "scan",
                  label: target.label,
                  occurredAt: target.occurredAt ?? null,
                  lat: target.lat,
                  lng: target.lng,
                }
              : null,
          nonce: previous.nonce + 1,
        }));
      } catch {
        if (activeSecurityContextRef.current !== cadSecurityContextKey) return;
        setActivityFocus(null);
        setActivityMapMessage("Could not load check-in map points for this capture.");
      }
    },
    [cadSecurityContextKey, client, config.accountId],
  );

  const activeMapFocus: CadMapFocus =
    mapFocus.securityContextKey === cadSecurityContextKey
      ? mapFocus
      : {
          securityContextKey: cadSecurityContextKey,
          subject: null,
          selectedEvent: null,
          nonce: mapFocus.nonce + 1,
        };

  return (
    <div className="cad">
      <CadDesk
        // Workspace switching and support sessions must discard every account-scoped read
        // (badge counts included) before the next security context's reads begin.
        key={cadSecurityContextKey}
        client={client}
        accountId={config.accountId}
        accountName={config.accountName}
        timeZone={config.displayTimeZone}
        pollMs={config.refreshMs}
        mapStyle={config.mapStyle}
        mapTilerKey={config.mapTilerKey}
        tomTomKey={config.tomTomKey}
        trafficEnabled={config.trafficEnabled !== false}
        trafficEnabledDefault={config.trafficEnabledDefault === true}
        showDutyPresence={showDutyPresence}
        canCreateDutyCallOffer={canCreateDutyCallOffer}
        canViewEvidence={canViewEvidence}
        mapFocus={activeMapFocus}
        onSelectRouteAssignment={focusRouteAssignment}
        onFocusAlertOnMap={focusAlertOnMap}
        onSelectActivity={focusActivityOnMap}
        selectedActivityEventId={selectedActivityEventId}
        activityMapMessage={activityMapMessage}
        activityFocus={activityFocus}
        onDismissActivityFocus={dismissActivityFocus}
        punchHighlight={punchHighlight}
        onDismissPunchHighlight={dismissPunchHighlight}
        onDismissFocus={dismissFocus}
        onFocusSite={focusSiteSubject}
        onFocusCallEvent={focusCallEventOnMap}
        requestedCadTile={requestedCadTile}
        requestedAlertEventId={requestedAlertEventId}
        requestAlertMapFocus={requestAlertMapFocus}
      />
    </div>
  );
}

function CadDesk({
  client,
  accountId,
  accountName,
  timeZone,
  pollMs,
  mapStyle,
  mapTilerKey,
  tomTomKey,
  trafficEnabled,
  trafficEnabledDefault,
  showDutyPresence,
  canCreateDutyCallOffer,
  canViewEvidence,
  mapFocus,
  onSelectRouteAssignment,
  onFocusAlertOnMap,
  onSelectActivity,
  selectedActivityEventId,
  activityMapMessage,
  activityFocus,
  onDismissActivityFocus,
  punchHighlight,
  onDismissPunchHighlight,
  onDismissFocus,
  onFocusSite,
  onFocusCallEvent,
  requestedCadTile,
  requestedAlertEventId,
  requestAlertMapFocus,
}: {
  client: SupabaseClient;
  accountId: string;
  accountName: string;
  timeZone: string;
  pollMs: number;
  mapStyle: ShellOutletContext["config"]["mapStyle"];
  mapTilerKey: string | undefined;
  tomTomKey: string | undefined;
  trafficEnabled: boolean;
  trafficEnabledDefault: boolean;
  showDutyPresence: boolean;
  canCreateDutyCallOffer: boolean;
  canViewEvidence: boolean;
  mapFocus: CadMapFocus;
  onSelectRouteAssignment: (routeAssignmentId: string) => void;
  onFocusAlertOnMap: (alert: AlertRow) => void;
  onSelectActivity: (row: ScanRow) => void;
  selectedActivityEventId: string | null;
  activityMapMessage: string | null;
  activityFocus: ActivityMapTarget | null;
  onDismissActivityFocus: () => void;
  punchHighlight: PunchMapHighlight | null;
  onDismissPunchHighlight: () => void;
  onDismissFocus: () => void;
  onFocusSite: (subject: Extract<CommandFocusSubject, { kind: "site" }>) => void;
  onFocusCallEvent: (call: DutyCallSummary, event: CommandFocusEventInput) => void;
  requestedCadTile: "alerts" | "sos" | null;
  requestedAlertEventId: string | null;
  requestAlertMapFocus: boolean;
}) {
  const feedOptions = { pollMs, enableRealtime: true as const };
  const selectedRouteAssignmentId =
    mapFocus.subject?.kind === "officer" ? mapFocus.subject.routeAssignmentId : null;
  const selectedSiteId = mapFocus.subject?.kind === "site" ? mapFocus.subject.siteId : null;
  // Dock badges: slower poll + realtime. Open tiles still use `pollMs` for their own reads.
  const badgePollMs = dockBadgePollMs(pollMs);
  const [dataRefreshToken, setDataRefreshToken] = useState(0);
  const [mapDataRefreshedAt, setMapDataRefreshedAt] = useState<number | null>(null);
  const [refreshBusy, setRefreshBusy] = useState(false);

  // One alert read feeds the status strip, the Alerts badge, and the SOS hot state, so the
  // strip and the SOS bubble can never disagree about what is open right now.
  const alertFeed = useReadModel(
    () => fetchAlertQueue(client, accountId),
    [client, accountId, dataRefreshToken],
    {
      pollMs,
      realtime: cadRealtimeFeed(client, accountId, "status", "alert_events"),
    },
  );
  const handledMapDeepLink = useRef<string | null>(null);

  useEffect(() => {
    if (!requestAlertMapFocus || requestedAlertEventId === null || alertFeed.data === null) {
      return;
    }
    const requestKey = `${accountId}:${requestedAlertEventId}`;
    if (handledMapDeepLink.current === requestKey) return;
    const selectedAlert = alertFeed.data.find(
      (alert) => alert.alertEventId === requestedAlertEventId,
    );
    if (selectedAlert === undefined) return;
    handledMapDeepLink.current = requestKey;
    onFocusAlertOnMap(selectedAlert);
  }, [
    accountId,
    alertFeed.data,
    onFocusAlertOnMap,
    requestAlertMapFocus,
    requestedAlertEventId,
  ]);
  // Badge-only reads. Distinct dock-* channels: the tile surfaces subscribe with their own
  // keys when open, and Supabase channels must not collide on one client.
  const rosterFeed = useReadModel(
    () => fetchRoster(client, accountId),
    [client, accountId, dataRefreshToken],
    {
      pollMs: badgePollMs,
      realtime: cadRealtimeFeed(client, accountId, "dock-roster", "route_assignments"),
    },
  );
  const activityFeed = useReadModel(
    () => fetchScanList(client, accountId),
    [client, accountId, dataRefreshToken],
    {
      pollMs: badgePollMs,
      realtime: cadRealtimeFeed(client, accountId, "dock-activity", "checkpoint_scans"),
    },
  );
  // A1 does not publish the Call spine to Realtime. Keep the dock honest with the same slow
  // badge polling fallback CAD uses elsewhere; the open Calls tile polls at the normal cadence.
  const callsFeed = useReadModel(
    () => fetchDutyCalls(client, accountId),
    [client, accountId, dataRefreshToken],
    badgePollMs,
  );
  // Account duty policy, read once for the whole desk. The getter admits any account member and
  // platform access, so every viewer who can see the map starts it: the GPS accuracy bands on
  // check-in dots must match the Timeclock column for a support viewer as much as for a
  // supervisor. A missing/failed read is passed as explicit null so CommandMap keeps Replay
  // closed and grades at the shipped 25 / 100 until the current account policy is known.
  const dutyPolicyFeed = useReadModel(
    () => fetchDutyTrackingPolicy(client, accountId),
    [client, accountId, dataRefreshToken],
    pollMs,
  );
  const dutyPolicy = dutyPolicyFeed.status === "ready" ? dutyPolicyFeed.data : null;

  const openAlerts = countOpenAlerts(alertFeed.data ?? []);
  // Design lock 7: hot is a real open SOS only, never a filter or a preview.
  const openSos = countOpenSosAlerts(alertFeed.data ?? []);
  const onShiftCount = countOnShift(rosterFeed.data ?? []);
  const recentActivityCount = countRecentScans(
    activityFeed.data ?? [],
    activityFeed.lastRefreshedAt ?? Date.now(),
  );
  const openDutyCalls = countOpenDutyCalls(callsFeed.data ?? []);

  const deskLastRefreshedAt = (() => {
    const stamps = [
      alertFeed.lastRefreshedAt,
      rosterFeed.lastRefreshedAt,
      activityFeed.lastRefreshedAt,
      callsFeed.lastRefreshedAt,
      mapDataRefreshedAt,
    ].filter((value): value is number => typeof value === "number" && Number.isFinite(value));
    if (stamps.length === 0) {
      return null;
    }
    return Math.max(...stamps);
  })();

  const refreshDesk = useCallback(() => {
    setRefreshBusy(true);
    setDataRefreshToken((value) => value + 1);
    // Clear busy after a short pulse so a fast refresh still feedbacks; feeds update stamp on success.
    window.setTimeout(() => setRefreshBusy(false), 700);
  }, []);

  return (
    <>
      <CadStatusBar
        accountName={accountName}
        timeZone={timeZone}
        alerts={alertFeed.data}
        alertsStatus={alertFeed.status}
        lastRefreshedAt={deskLastRefreshedAt}
        realtimeConnected={alertFeed.realtimeConnected}
        onRefresh={refreshDesk}
        refreshBusy={refreshBusy}
      />
      <section className="cad__stage" aria-label="Command map">
        <Suspense
          fallback={
            <p className="surface__state" role="status">
              Loading map…
            </p>
          }
        >
          <CommandMap
            client={client}
            accountId={accountId}
            mapStyle={mapStyle}
            showDutyPings={showDutyPresence}
            // Replay and the Live check-in window stay behind the duty-presence capability, as
            // before: a viewer without it still gets explicit null here (Replay closed).
            replayRetentionDays={
              showDutyPresence && dutyPolicy !== null ? dutyPolicy.replayRetentionDays : null
            }
            liveCheckinMaxAgeHours={
              showDutyPresence && dutyPolicy !== null ? dutyPolicy.liveCheckinMaxAgeHours : null
            }
            // Same policy read feeds the pin identity bands for every viewer, so Live and
            // Timeclock cannot disagree about the same metres. Not ready is explicit null
            // (fail closed to 25 / 100).
            gpsAccuracyBands={
              dutyPolicy !== null
                ? gpsAccuracyBandThresholds(
                    dutyPolicy.gpsAccuracyHighMaxMeters,
                    dutyPolicy.gpsAccuracyMediumMaxMeters,
                  )
                : null
            }
            timeZone={timeZone}
            pollMs={pollMs}
            dataRefreshToken={dataRefreshToken}
            onMapDataRefreshed={setMapDataRefreshedAt}
            focusRouteAssignmentId={selectedRouteAssignmentId}
            focusSiteId={selectedSiteId}
            focusRequestNonce={mapFocus.nonce}
            focusSubject={mapFocus.subject}
            focusSelectedEvent={mapFocus.selectedEvent}
            focusAlerts={alertFeed.data ?? []}
            // Already in memory for the Calls tile badge. The Replay Call picker reads this feed
            // rather than opening a second copy of it, so the picker cannot offer a Call the
            // Calls desk beside it is not showing.
            dutyCalls={callsFeed.data ?? []}
            onDismissFocus={onDismissFocus}
            onFocusSite={onFocusSite}
            punchHighlight={punchHighlight}
            onDismissPunchHighlight={onDismissPunchHighlight}
            requestedCadTile={requestedCadTile}
            activityFocus={activityFocus}
            onDismissActivityFocus={onDismissActivityFocus}
            {...(mapTilerKey !== undefined ? { mapTilerKey } : {})}
            {...(tomTomKey !== undefined ? { tomTomKey } : {})}
            trafficEnabled={trafficEnabled}
            trafficEnabledDefault={trafficEnabledDefault}
            cadTiles={{
              onshift: {
                // Zero work: no badge (empty-duty lock 3). CadDock already suppresses 0.
                ...(onShiftCount > 0 ? { badge: onShiftCount } : {}),
                subtitle:
                  onShiftCount === 0
                    ? `Nobody on shift · ${accountName}`
                    : `${onShiftCount === 1 ? "1 officer" : `${onShiftCount} officers`} on shift · ${accountName}`,
                body: (
                  <OnShiftRoster
                    key={dataRefreshToken}
                    client={client}
                    accountId={accountId}
                    timeZone={timeZone}
                    showDutyPresence={showDutyPresence}
                    selectedRouteAssignmentId={selectedRouteAssignmentId}
                    onSelectRouteAssignment={onSelectRouteAssignment}
                    {...feedOptions}
                  />
                ),
              },
              alerts: {
                ...(openAlerts > 0 ? { badge: openAlerts, badgeTone: "warn" as const } : {}),
                subtitle: openAlerts > 0 ? `${openAlerts} open` : "None open",
                body: (
                  <AlertQueue
                    key={dataRefreshToken}
                    client={client}
                    accountId={accountId}
                    timeZone={timeZone}
                    selectedRouteAssignmentId={selectedRouteAssignmentId}
                    selectedAlertEventId={requestedAlertEventId}
                    onFocusOnMap={onFocusAlertOnMap}
                    {...feedOptions}
                  />
                ),
                foot: "Highest priority first. Acknowledge from the row once a person owns it.",
              },
              calls: {
                ...(openDutyCalls > 0 ? { badge: openDutyCalls } : {}),
                subtitle:
                  openDutyCalls === 0
                    ? "No open Calls"
                    : `${openDutyCalls} open ${openDutyCalls === 1 ? "Call" : "Calls"}`,
                body: (
                  <DutyCallsDesk
                    key={dataRefreshToken}
                    client={client}
                    accountId={accountId}
                    timeZone={timeZone}
                    canCreateOffer={canCreateDutyCallOffer}
                    canViewEvidence={canViewEvidence}
                    pollMs={pollMs}
                    onShowEventOnMap={onFocusCallEvent}
                  />
                ),
                // The Incident clause rides with the section it describes: without evidence.view
                // there is no receipts section, and a footer about Incidents would explain
                // something this operator can never see on the tile.
                foot: canViewEvidence
                  ? "Call status comes from its append-only trail. Requesting backup is an amber side state and never replaces the linear status. Incidents are separate reports that reference this Call and keep their own review status."
                  : "Call status comes from its append-only trail. Requesting backup is an amber side state and never replaces the linear status.",
              },
              activity: {
                ...(recentActivityCount > 0 ? { badge: recentActivityCount } : {}),
                subtitle:
                  recentActivityCount > 0
                    ? `${recentActivityCount} in the last hour`
                    : "Quiet hour",
                body: (
                  <>
                    {activityMapMessage !== null ? (
                      <p className="surface__state" role="status">
                        {activityMapMessage}
                      </p>
                    ) : null}
                    <LiveActivity
                      key={dataRefreshToken}
                      client={client}
                      accountId={accountId}
                      timeZone={timeZone}
                      scopeToken={String(dataRefreshToken)}
                      onScopeChange={onDismissActivityFocus}
                      onSelect={onSelectActivity}
                      selectedClientEventId={selectedActivityEventId}
                      {...feedOptions}
                    />
                  </>
                ),
                foot: "Green marks a capture the device verified. Flagged items stay neutral until a person reviews them.",
              },
              sos: {
                hot: openSos > 0,
                ...(openSos > 0 ? { badge: openSos, badgeTone: "hot" as const } : {}),
                subtitle: openSos > 0 ? `${openSos} open` : "No active SOS",
                body: (
                  <SosResponseDeskPanel
                    key={dataRefreshToken}
                    client={client}
                    accountId={accountId}
                    timeZone={timeZone}
                    pollMs={pollMs}
                    selectedAlertEventId={requestedAlertEventId}
                  />
                ),
                foot: "This bubble turns red and breathes only while an SOS is open. It is not a filter and never lights up for anything else.",
              },
            }}
          />
        </Suspense>
      </section>
    </>
  );
}
